/**
 * The tenant's commercial rules, apart from the calibrated model: the uplift applied last, and
 * the governance (price rules and approval limits). Saved directly, audited, applied from the
 * next price; recorded quotes keep the prices they were given.
 */
import { authorityOf, splitSubKey, subKey, type AuthorityLimit, type AuthorityLimits, type LimitRef, type LimitUnit, type Policy, type PricingRules, type Uplift } from '@quoting/ladder'
import { useCallback, useEffect, useState } from 'react'
import { cx, ErrorBox, Field, Panel, ScreenHeading, Spinner } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useT } from '../../lib/i18n'

const fmtNum = (n: number, digits = 3) => {
  const s = n.toFixed(digits)
  return s.replace(/\.?0+$/, '')
}

/** Load the rules, keep an edited copy, save or discard it. */
function useRules() {
  const [saved, setSaved] = useState<PricingRules | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const load = useCallback(() => api.get<PricingRules>('/api/admin/rules').then(setSaved).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    load()
  }, [load])
  const put = async (url: string, body: unknown) => {
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      setSaved(await api.put<PricingRules>(url, body))
      setDone(true)
      return true
    } catch (e) {
      setError(errorMessage(e))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { saved, error, busy, done, setDone, put }
}

function SaveBar({ dirty, busy, done, onSave, onDiscard }: { dirty: boolean; busy: boolean; done: boolean; onSave: () => void; onDiscard: () => void }) {
  const { t } = useT()
  return (
    <div className="flex items-center gap-2">
      {done && !dirty && <span className="text-xs text-emerald-600">{t('Saved. Applies from the next price.')}</span>}
      {dirty && (
        <button type="button" className="btn" disabled={busy} onClick={onDiscard}>
          {t('Discard changes')}
        </button>
      )}
      <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={onSave}>
        {busy ? t('Saving…') : t('Save')}
      </button>
    </div>
  )
}

export function UpliftScreen() {
  const { t } = useT()
  const { saved, error, busy, done, setDone, put } = useRules()
  const [draft, setDraft] = useState<Uplift | null>(null)
  const [key, setKey] = useState(0)
  if (!saved) return error ? <ErrorBox>{error}</ErrorBox> : <Spinner />
  const value = draft ?? saved.uplift
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved.uplift)
  return (
    <div className="space-y-4">
      <ScreenHeading
        title={t('Commercial uplift')}
        right={
          <SaveBar
            dirty={dirty}
            busy={busy}
            done={done}
            onSave={async () => {
              if (await put('/api/admin/rules/uplift', { uplift: value })) setDraft(null)
            }}
            onDiscard={() => {
              setDraft(null)
              setKey((k) => k + 1)
            }}
          />
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      <UpliftEditor
        key={key}
        value={value}
        disabled={busy}
        onChange={(u) => {
          setDraft(u)
          setDone(false)
        }}
      />
    </div>
  )
}

export function GovernanceScreen() {
  const { t } = useT()
  const { saved, error, busy, done, setDone, put } = useRules()
  const [draft, setDraft] = useState<Policy | null>(null)
  const [key, setKey] = useState(0)
  if (!saved) return error ? <ErrorBox>{error}</ErrorBox> : <Spinner />
  const value = draft ?? saved.policy
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved.policy)
  const change = (p: Policy) => {
    setDraft(p)
    setDone(false)
  }
  return (
    <div className="space-y-4">
      <ScreenHeading
        title={t('Governance')}
        right={
          <SaveBar
            dirty={dirty}
            busy={busy}
            done={done}
            onSave={async () => {
              const body = saved.policyByCategory ? { policy: value, policyByCategory: saved.policyByCategory } : { policy: value }
              if (await put('/api/admin/rules/governance', body)) setDraft(null)
            }}
            onDiscard={() => {
              setDraft(null)
              setKey((k) => k + 1)
            }}
          />
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      <div key={key} className="space-y-4">
        <AuthorityEditor value={authorityOf(value)} disabled={busy} onChange={(limits) => change({ ...value, authority: { ...value.authority, limits } })} />
        <PriceRules value={value} disabled={busy} onChange={change} />
      </div>
    </div>
  )
}

/** A rate entered as a percentage, kept as typed so "5," is not reformatted mid-entry. */
function PctInput({ value, disabled, onChange }: { value: number; disabled: boolean; onChange: (v: number) => void }) {
  const [text, setText] = useState<string | null>(null)
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        className="field w-28 text-right tabular"
        inputMode="decimal"
        disabled={disabled}
        value={text ?? fmtNum(value * 100, 2)}
        onChange={(e) => {
          setText(e.target.value)
          const n = Number(e.target.value.replace(',', '.'))
          if (Number.isFinite(n) && e.target.value.trim() !== '') onChange(n / 100)
        }}
        onBlur={() => setText(null)}
      />
      <span className="text-slate-500">%</span>
    </span>
  )
}

const BLOCK_LABEL: Record<string, string> = { market: 'Market situation', structure: 'Structural position', client: 'Client context', deal: 'Deal context' }

function PriceRules({ value, disabled, onChange }: { value: Policy; disabled: boolean; onChange: (p: Policy) => void }) {
  const { t } = useT()
  const set = <K extends keyof Policy>(k: K, v: Policy[K]) => onChange({ ...value, [k]: v })
  return (
    <Panel title={t('Price rules')}>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label={t('Ask above target')} hint={t('The ask is the target plus this share: the room to negotiate down to target.')}>
          <PctInput value={value.concessionRate} disabled={disabled} onChange={(v) => set('concessionRate', v)} />
        </Field>
        <Field label={t('Corridor cap')} hint={t('The target rises at most this much above the customer’s last price, adjusted for cost.')}>
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="field w-44"
              disabled={disabled}
              value={value.capRate === null ? 'none' : 'cap'}
              onChange={(e) => set('capRate', e.target.value === 'none' ? null : 0.08)}
            >
              <option value="cap">{t('Capped at')}</option>
              <option value="none">{t('No ceiling')}</option>
            </select>
            {value.capRate !== null && <PctInput value={value.capRate} disabled={disabled} onChange={(v) => set('capRate', v)} />}
          </div>
        </Field>
        <Field label={t('Floor includes')} hint={t('The floor is cost plus these blocks and every € surcharge.')}>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {(['market', 'structure', 'client', 'deal'] as const).map((b) => (
              <label key={b} className="flex items-center gap-1.5 text-sm text-slate-300">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={value.floorIncludesBlocks.includes(b)}
                  onChange={(e) => set('floorIncludesBlocks', e.target.checked ? [...value.floorIncludesBlocks, b] : value.floorIncludesBlocks.filter((x) => x !== b))}
                />
                {t(BLOCK_LABEL[b]!)}
              </label>
            ))}
          </div>
        </Field>
        <Field label={t('Cost movement since the last price')} hint={t('How the corridor carries cost changes since the customer’s last price.')}>
          <select className="field w-72" disabled={disabled} value={value.costMoveBasis} onChange={(e) => set('costMoveBasis', e.target.value as Policy['costMoveBasis'])}>
            <option value="cost">{t('Pass on the cost change in €')}</option>
            <option value="price">{t('Pass on the cost change as a % of the price')}</option>
          </select>
        </Field>
      </div>
    </Panel>
  )
}

interface UpliftRow {
  scope: 'base' | 'cat' | 'sub'
  category: string
  subCategory: string
  /** percent as typed */
  pct: string
}

function upliftRows(u: Uplift): UpliftRow[] {
  const rows: UpliftRow[] = []
  if (u.base !== undefined) rows.push({ scope: 'base', category: '', subCategory: '', pct: fmtNum(u.base * 100, 2) })
  for (const [k, v] of Object.entries(u.cat ?? {})) rows.push({ scope: 'cat', category: k, subCategory: '', pct: fmtNum(v * 100, 2) })
  for (const [k, v] of Object.entries(u.sub ?? {})) {
    const { category, subCategory } = splitSubKey(k)
    rows.push({ scope: 'sub', category, subCategory, pct: fmtNum(v * 100, 2) })
  }
  return rows
}

function upliftOf(rows: UpliftRow[]): Uplift {
  const u: Uplift = {}
  for (const r of rows) {
    const n = Number(r.pct.replace(',', '.'))
    if (!Number.isFinite(n) || r.pct.trim() === '') continue
    if (r.scope === 'base') u.base = n / 100
    else if (r.scope === 'cat' && r.category.trim()) u.cat = { ...u.cat, [r.category.trim()]: n / 100 }
    else if (r.scope === 'sub' && r.category.trim() && r.subCategory.trim()) u.sub = { ...u.sub, [subKey(r.category.trim(), r.subCategory.trim())]: n / 100 }
  }
  return u
}

/** The commercial uplift as a table: one rate for everything, per category, per sub-category; the most specific applies. */
function UpliftEditor({ value, disabled, onChange }: { value: Uplift; disabled: boolean; onChange: (u: Uplift) => void }) {
  const { t } = useT()
  const [rows, setRows] = useState<UpliftRow[]>(() => upliftRows(value))
  const [cats, setCats] = useState<{ category: string; subCategory: string }[]>([])
  useEffect(() => {
    api
      .get<{ category: string; subCategory: string }[]>('/api/products?limit=200')
      .then((ps) => {
        const seen = new Map<string, { category: string; subCategory: string }>()
        for (const p of ps) seen.set(subKey(p.category, p.subCategory), { category: p.category, subCategory: p.subCategory })
        setCats([...seen.values()])
      })
      .catch(() => {})
  }, [])
  const update = (next: UpliftRow[]) => {
    setRows(next)
    onChange(upliftOf(next))
  }
  const set = (i: number, patch: Partial<UpliftRow>) => update(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const categories = [...new Set(cats.map((c) => c.category))]
  const scopeLabel = { base: t('All articles'), cat: t('Category'), sub: t('Sub-category') }
  return (
    <Panel>
      <datalist id="uplift-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 font-medium">{t('Applies to')}</th>
            <th className="py-2 pr-3 font-medium">{t('Category')}</th>
            <th className="py-2 pr-3 font-medium">{t('Sub-category')}</th>
            <th className="py-2 pr-3 text-right font-medium">{t('Uplift')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-800">
              <td className="py-1.5 pr-3">
                <select className="field" disabled={disabled} value={r.scope} onChange={(e) => set(i, { scope: e.target.value as UpliftRow['scope'] })}>
                  {(['base', 'cat', 'sub'] as const).map((s) => (
                    <option key={s} value={s} disabled={s === 'base' && r.scope !== 'base' && rows.some((x) => x.scope === 'base')}>{scopeLabel[s]}</option>
                  ))}
                </select>
              </td>
              <td className="py-1.5 pr-3">
                {r.scope !== 'base' && <input className="field" list="uplift-cats" disabled={disabled} value={r.category} onChange={(e) => set(i, { category: e.target.value })} />}
              </td>
              <td className="py-1.5 pr-3">
                {r.scope === 'sub' && (
                  <>
                    <datalist id={`uplift-subs-${i}`}>
                      {cats.filter((c) => c.category === r.category).map((c) => <option key={c.subCategory} value={c.subCategory} />)}
                    </datalist>
                    <input className="field" list={`uplift-subs-${i}`} disabled={disabled} value={r.subCategory} onChange={(e) => set(i, { subCategory: e.target.value })} />
                  </>
                )}
              </td>
              <td className="py-1.5 pr-3 text-right">
                <span className="inline-flex items-center gap-1.5">
                  <input className="field w-24 text-right tabular" disabled={disabled} value={r.pct} onChange={(e) => set(i, { pct: e.target.value })} />
                  <span className="text-slate-500">%</span>
                </span>
              </td>
              <td className="py-1.5 text-right">
                {!disabled && (
                  <button type="button" className="btn !py-0.5 text-xs" onClick={() => update(rows.filter((_, j) => j !== i))}>
                    {t('Remove')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && (
        <button
          type="button"
          className="btn mt-3 !py-0.5 text-xs"
          onClick={() => setRows([...rows, { scope: rows.some((x) => x.scope === 'base') ? 'cat' : 'base', category: '', subCategory: '', pct: '' }])}
        >
          {t('Add row')}
        </button>
      )}
    </Panel>
  )
}

const ROLES: { key: keyof AuthorityLimits; label: string }[] = [
  { key: 'rep', label: 'Sales rep' },
  { key: 'supervisor', label: 'Sales director' },
  { key: 'head', label: 'General manager' },
]
const REFS: { v: LimitRef; label: string }[] = [
  { v: 'ask', label: 'Ask' },
  { v: 'target', label: 'Target' },
  { v: 'floor', label: 'Floor' },
  { v: 'cost', label: 'Cost' },
]
const UNITS: { v: LimitUnit; label: string }[] = [
  { v: 'pct', label: '% of the reference' },
  { v: 'eur', label: '€ per unit' },
  { v: 'drivers', label: '% of the driver sum' },
]

/** One row per role: the lowest price the role accepts alone, measured from a reference price. */
function AuthorityEditor({ value, disabled, onChange }: { value: AuthorityLimits; disabled: boolean; onChange: (v: AuthorityLimits) => void }) {
  const parent: AuthorityLimits | null = null
  const { t } = useT()
  const setRole = (k: keyof AuthorityLimits, l: AuthorityLimit | null) => onChange({ ...value, [k]: l } as AuthorityLimits)
  const shown = (l: AuthorityLimit) => (l.unit === 'eur' ? Math.abs(l.below) : Math.abs(l.below) * 100)
  // direction and the typed amount are kept locally, so a zero amount does not lose "above" and typing "5," is not reformatted
  const [dirs, setDirs] = useState<Record<string, 'below' | 'above'>>(() =>
    Object.fromEntries(ROLES.map(({ key }) => [key, (value[key]?.below ?? 0) < 0 ? 'above' : 'below'])),
  )
  const [texts, setTexts] = useState<Record<string, string>>({})
  const sign = (k: string) => (dirs[k] === 'above' ? -1 : 1)
  const describe = (l: AuthorityLimit | null) => {
    if (!l) return t('no limit')
    const ref = t(REFS.find((r) => r.v === l.ref)!.label)
    if (l.below === 0) return ref
    const amt = l.unit === 'eur' ? `${fmtNum(Math.abs(l.below), 2)} €` : `${fmtNum(Math.abs(l.below) * 100, 2)} %${l.unit === 'drivers' ? ` ${t('of the driver sum')}` : ''}`
    return `${ref} ${l.below > 0 ? '−' : '+'} ${amt}`
  }
  return (
    <Panel title={t('Approval limits')} subtitle={t('The lowest price each role accepts on its own. Below the sales director’s limit the general manager decides; below the general manager’s limit nobody can.')}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="label pb-2 font-medium">{t('Role')}</th>
            <th className="label pb-2 font-medium">{t('Reference')}</th>
            <th className="label pb-2 font-medium">{t('Direction')}</th>
            <th className="label pb-2 font-medium">{t('Amount')}</th>
            <th className="label pb-2 font-medium">{t('Unit')}</th>
            <th className="label pb-2 font-medium">{parent ? t('Parent') : ''}</th>
          </tr>
        </thead>
        <tbody>
          {ROLES.map(({ key, label }) => {
            const l = value[key]
            const pl = parent ? parent[key] : undefined
            const moved = parent !== null && JSON.stringify(pl) !== JSON.stringify(l)
            return (
              <tr key={key} className={cx('border-t border-slate-800', moved && 'bg-amber-500/5')}>
                <td className="py-2 pr-3 font-medium text-slate-200">{t(label)}</td>
                <td className="py-2 pr-3">
                  <select
                    className="field w-40"
                    disabled={disabled}
                    value={l ? l.ref : 'none'}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === 'none') setRole(key, null)
                      else setRole(key, { ref: v as LimitRef, unit: l?.unit ?? 'pct', below: l?.below ?? 0 })
                    }}
                  >
                    {key === 'head' && <option value="none">{t('no limit')}</option>}
                    {REFS.map((r) => (
                      <option key={r.v} value={r.v}>{t(r.label)}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  {l && (
                    <select
                      className="field w-32"
                      disabled={disabled}
                      value={dirs[key] ?? 'below'}
                      onChange={(e) => {
                        const d = e.target.value as 'below' | 'above'
                        setDirs((x) => ({ ...x, [key]: d }))
                        setRole(key, { ...l, below: (d === 'above' ? -1 : 1) * Math.abs(l.below) })
                      }}
                    >
                      <option value="below">{t('below')}</option>
                      <option value="above">{t('above')}</option>
                    </select>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {l && (
                    <input
                      className="field w-28 text-right tabular"
                      disabled={disabled}
                      inputMode="decimal"
                      value={texts[key] ?? fmtNum(shown(l), 3)}
                      onChange={(e) => {
                        const text = e.target.value
                        setTexts((x) => ({ ...x, [key]: text }))
                        const n = Number(text.replace(',', '.'))
                        if (!Number.isFinite(n)) return
                        const mag = l.unit === 'eur' ? Math.abs(n) : Math.abs(n) / 100
                        setRole(key, { ...l, below: sign(key) * mag })
                      }}
                      onBlur={() => setTexts((x) => ({ ...x, [key]: undefined as unknown as string }))}
                    />
                  )}
                </td>
                <td className="py-2 pr-3">
                  {l && (
                    <select className="field w-52" disabled={disabled} value={l.unit} onChange={(e) => setRole(key, { ...l, unit: e.target.value as LimitUnit })}>
                      {UNITS.map((u) => (
                        <option key={u.v} value={u.v}>
                          {u.v === 'pct' ? t('% of {ref}', { ref: t(REFS.find((r) => r.v === l.ref)!.label) }) : t(u.label)}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="py-2 text-xs text-slate-500">{parent ? <span className={moved ? 'text-amber-500' : ''}>{describe(pl ?? null)}</span> : null}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="mt-3 text-[12px] text-slate-500">
        {ROLES.map(({ key, label }) => `${t(label)}: ${describe(value[key])}`).join(' · ')}
      </p>
    </Panel>
  )
}
